import { Query } from '@apollo/client/react/components';
import { SiteUrls } from '@tableau/shared-urls';
import { IDataQualityIndicator } from '@tableau/vizportal-webclient-api/lib/api/types/IDataQualityIndicator';
import React from 'react';

import {
  VirtualConnectionLineageUpstreamDataQualityWarningsQuery,
  VirtualConnectionLineageUpstreamDataQualityWarningsQueryVariables,
  VirtualConnectionLineageUpstreamDataQualityWarningsDocument
} from '../../../../../gen-src/queries';
import {
  AssetMarkedByIndicator,
  DataQualityDetailProps,
  VizPortalDQWDataCatalogProvider,
  WarningStatusChip,
  WarningStatusProps
} from '../../../data-catalog-shared';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { RenderQueryResult } from '../renderQueryResult';
import { emptyWarnings, filterOutWarningsFromAnchorUntilCycleIsFixed } from './DataQualityWarningCycleTemporaryFix';
import { EmbeddableDataQualityWarningProps } from './EmbeddableDataQualityWarningProps';

interface LineageWarningVirtualConnectionProps {
  readonly asset: AssetMarkedByIndicator;
  readonly vizportalUrlId: string;
  readonly ownContentWarnings: ReadonlyArray<DataQualityDetailProps>;
  readonly anchoredWarnings: ReadonlyArray<IDataQualityIndicator>;
}

export type EmbeddableVirtualConnectionDataQualityWarningProps = EmbeddableDataQualityWarningProps & LineageWarningVirtualConnectionProps;

/** @deprecated TFS1478812 */
export function EmbeddableVirtualConnectionDataQualityWarning(props: EmbeddableVirtualConnectionDataQualityWarningProps): JSX.Element {
  const { source, siteUrls: urls } = props;

  return (
    <VizPortalDQWDataCatalogProvider {...props}>
      <ErrorListProvider>
        <Query<VirtualConnectionLineageUpstreamDataQualityWarningsQuery, VirtualConnectionLineageUpstreamDataQualityWarningsQueryVariables>
          query={VirtualConnectionLineageUpstreamDataQualityWarningsDocument}
          variables={{ vizportalUrlId: props.vizportalUrlId, first: 10, offset: 0 }}
        >
          {result => <RenderQueryResult result={result} renderers={{
            renderLoading: () => renderWarnings(urls, { ...props }, source),
            renderData: data => renderWarnings(urls, { ...props }, source, data),
          }} />}
        </Query>
      </ErrorListProvider>
    </VizPortalDQWDataCatalogProvider>
  );
}

function renderWarnings(
  urls: SiteUrls,
  args: Pick<EmbeddableVirtualConnectionDataQualityWarningProps, 'asset' | 'anchoredWarnings'>,
  source?: string,
  data?: VirtualConnectionLineageUpstreamDataQualityWarningsQuery
): JSX.Element | null {
  const virtualConnection = data && data.virtualConnections ? data.virtualConnections[0] : null;
  const anchoredWarnings = args.anchoredWarnings.filter(w => w.isActive)
  if (!virtualConnection && anchoredWarnings.length < 1) {
    return null;
  }

  const upstreamWarnings = virtualConnection
    ? filterOutWarningsFromAnchorUntilCycleIsFixed(virtualConnection.id, virtualConnection.upstreamDataQualityWarningsConnection?.nodes ?? emptyWarnings)
    : emptyWarnings

  const props: WarningStatusProps = {
    subjectArea: 'VirtualConnection',
    kind: 'vizportal',
    anchoredAsset: args.asset,
    iconOnly: false,
    anchoredWarnings,
    upstreamWarnings,
    urls,
    source: source ?? 'EmbeddableVirtualConnectionDataQualityWarning'
  }

  return <WarningStatusChip {...props} />
}
